import { auGenreIds, genreIds } from "@features/genreEvents/utils/genreIds";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import * as NavigationMenu from "@radix-ui/react-navigation-menu";
import { BasketSummary } from "@types";
import { COUNTRY_CODE } from "@utils/constants";
import { basketSummaryFetcher, handle429s } from "@utils/fetchers";
import { joinClassNames } from "@utils/helpers";
import {
  useLatestCookieValue,
  usePrevious,
  useSupabaseSession,
  useWindowSize,
} from "@utils/hooks";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useLayoutEffect, useRef, useState } from "react";
import useSWR from "swr";

/**
 * A navigation menu for genres. This is using radix react navigation menu UI alongside
 * framer motion to create the smooth background animation when hovering. Documentation for react nav menu https://www.radix-ui.com/docs/primitives/components/navigation-menu
 *
 */
export default function GenreNavMenu() {
  const basketKey = useLatestCookieValue("ASP.NET_SessionId");
  const pathname = usePathname();
  const windowSize = useWindowSize();
  const containerRef = useRef<HTMLDivElement>(null);
  const session = useSupabaseSession();
  const [hoveredGenre, setHoveredGenre] = useState<{
    width: number;
    x: number;
  } | null>(null);
  const lastHovered = usePrevious(hoveredGenre).current;
  const t = useTranslations("GenrePage.genres");
  const [containerWidth, setContainerWidth] = useState(800);
  const [listItemHovered, setListItemHovered] = useState(false);

  const { data: basket } = useSWR<BasketSummary>(
    basketKey ? `basket/${basketKey}/summary` : null,
    basketSummaryFetcher,
    {
      onErrorRetry: handle429s,
    }
  );

  const cont_width =
    (windowSize.width ? Math.min(windowSize.width, 1280) : 1280) - 64;
  const nav_width =
    cont_width -
    320 -
    (basket && basket.Quantity > 0 ? 122 : 0) -
    (!!session ? 0 : 73.19);

  const genres = COUNTRY_CODE === "au" ? auGenreIds : genreIds;

  useLayoutEffect(() => {
    const current = containerRef.current;
    if (current) {
      const handleResize = () => {
        setContainerWidth(current.getBoundingClientRect().width + 8);
      };

      window.addEventListener("resize", handleResize);

      handleResize();

      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  const genre_links = Object.entries(genres).filter(
    ([_, value]) => value.id !== 0
  );

  return (
    <>
      <div ref={containerRef} className="absolute invisible flex items-center">
        {Object.entries(genres).map(([key, value]) => (
          <span key={key} className="relative block z-10 px-4 py-2 font-medium">
            {t(value.translationKey)}
          </span>
        ))}
      </div>
      <div className="w-full">
        <AnimatePresence mode="wait" initial={false}>
          {nav_width <= containerWidth ? (
            <motion.div
              key="popover"
              initial={{ opacity: 0 }}
              animate={{ opacity: 100 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.1,
              }}
            >
              <NavigationMenu.Root className="relative">
                <NavigationMenu.List className="relative flex items-center">
                  <NavigationMenu.Item>
                    <NavigationMenu.Trigger
                      className={joinClassNames(
                        "relative flex items-center gap-4 z-10 px-4 py-2 font-medium hover:text-indigo-400 transition-colors ease-in-out duration-200",
                        pathname.includes("/whats-on/")
                          ? "text-indigo-400"
                          : "text-gray-400"
                      )}
                    >
                      {t("trigger")}
                      <ChevronDownIcon className="h-4 w-4" />
                    </NavigationMenu.Trigger>
                    <NavigationMenu.Content className="absolute top-[calc(100%+5px)] left-0 grid grid-cols-2 gap-x-2 p-2 rounded-2xl bg-white ring-1 ring-inset ring-gray-100 shadow">
                      {genre_links.map(([key, value]) => (
                        <NavigationMenu.Link key={key} asChild>
                          <Link
                            data-testid={t(value.translationKey)}
                            id={key}
                            onMouseEnter={() => {
                              setListItemHovered(true);
                            }}
                            onMouseLeave={() => {
                              setListItemHovered(false);
                            }}
                            className={joinClassNames(
                              "relative block z-10 px-4 py-2 rounded-xl font-medium hover:text-indigo-800 hover:bg-indigo-100 transition-colors ease-in-out duration-400",
                              pathname === `/whats-on/${key}`
                                ? "text-indigo-800"
                                : "text-gray-500",
                              pathname === `/whats-on/${key}` &&
                                !listItemHovered
                                ? "bg-indigo-100"
                                : "bg-white"
                            )}
                            href={`/whats-on/${key}`}
                          >
                            {t(value.translationKey)}
                          </Link>
                        </NavigationMenu.Link>
                      ))}
                    </NavigationMenu.Content>
                  </NavigationMenu.Item>
                  <NavigationMenu.Item key="whatson">
                    <Link
                      id="whatson"
                      className={joinClassNames(
                        "relative block z-10 px-4 py-2 font-medium rounded-lg text-indigo-400 border transition-colors ease-in-out duration-200",
                        pathname === `/whats-on`
                          ? "border-indigo-400"
                          : "border-indigo-500 hover:border-indigo-400"
                      )}
                      href="/whats-on"
                    >
                      {t("whatson")}
                    </Link>
                  </NavigationMenu.Item>
                </NavigationMenu.List>
              </NavigationMenu.Root>
            </motion.div>
          ) : (
            <motion.div
              key="bar"
              initial={{ opacity: 0 }}
              animate={{ opacity: 100 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.1,
              }}
            >
              <NavigationMenu.Root>
                <NavigationMenu.List className="relative flex items-center">
                  {genre_links.map(([key, value], index) => (
                    <NavigationMenu.Item key={key}>
                      <Link
                        data-testid={t(value.translationKey)}
                        id={key}
                        className={joinClassNames(
                          "relative block z-10 px-4 py-2 font-medium hover:text-indigo-400 transition-colors ease-in-out duration-200",
                          pathname === `/whats-on/${key}`
                            ? "text-indigo-400"
                            : "text-gray-400"
                        )}
                        onMouseEnter={() => {
                          const width =
                            document
                              .getElementById(key)
                              ?.getBoundingClientRect().width ?? 0;
                          let x = 0;
                          for (let i = 0; i < index; ++i) {
                            x +=
                              document
                                .getElementById(Object.keys(genres)[i])
                                ?.getBoundingClientRect().width ?? 0;
                          }
                          setHoveredGenre({ width, x });
                        }}
                        onMouseLeave={() => setHoveredGenre(null)}
                        href={`/whats-on/${key}`}
                      >
                        <span className="line-clamp-1">
                          {t(value.translationKey)}
                        </span>
                      </Link>
                    </NavigationMenu.Item>
                  ))}
                  <NavigationMenu.Item key="whatson">
                    <Link
                      id="whatson"
                      className={joinClassNames(
                        "relative block z-10 px-4 py-2 font-medium rounded-lg text-indigo-400 border transition-colors ease-in-out duration-200",
                        pathname === `/whats-on`
                          ? "border-indigo-400"
                          : "border-indigo-500 hover:border-indigo-400"
                      )}
                      href="/whats-on"
                    >
                      <span className="truncate">{t("whatson")}</span>
                    </Link>
                  </NavigationMenu.Item>
                  <motion.li
                    animate={
                      hoveredGenre
                        ? {
                            width: hoveredGenre.width,
                            x: hoveredGenre.x,
                            opacity: 1,
                          }
                        : {
                            width: lastHovered?.width,
                            x: lastHovered?.x,
                            opacity: 0,
                            transition: { opacity: { duration: 0.5 } },
                          }
                    }
                    transition={{ ease: "easeInOut", duration: 0.2 }}
                    className="absolute h-10 rounded-lg top-0 bg-indigo-950 pointer-events-none"
                  />
                </NavigationMenu.List>
              </NavigationMenu.Root>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
